import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SpinnerComponent } from './spinner.component';
import {
  Router,
  RouterEvent,
  ActivationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError
} from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class SpinnerService implements OnDestroy {
  protected destroy$: Subject<boolean> = new Subject<boolean>();
  private timer: any;

  overlayRef: OverlayRef;
  loaderSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loader$ = this.loaderSubject.asObservable();

  constructor(
    private overlay: Overlay,
    private router: Router,
    private ngZone: NgZone
  ) {}

  public initRouterEventSpinner(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: RouterEvent) => this.checkRouterEvent(event));
  }

  private checkRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof ActivationStart) {
      this.loading();
      return;
    }

    if (
      routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError
    ) {
      this.complete();
    }
  }

  loading() {
    if (this.overlayRef) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.overlayRef = this.overlay.create({
          positionStrategy: this.overlay.position().global(),
          scrollStrategy: this.overlay.scrollStrategies.block(),
          hasBackdrop: true
        });
        const ref = this.overlayRef.attach(
          new ComponentPortal(SpinnerComponent)
        );
        ref.instance.centerPos = true;
        ref.changeDetectorRef.detectChanges();
        this.loaderSubject.next(true);
      }, 200);
    });
  }

  complete(): void {
    clearTimeout(this.timer);
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
    }
    this.loaderSubject.next(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
